/**
 * Standard vs Actual Cost Variance — per-FG-lot, with totals.
 *
 * Compares each FG lot's actual cost-per-kg against the configured
 * StandardCostRate for its (count, yarn_type). Splits variance into
 * RM and Conversion legs so management can see which side is drifting:
 *   RM variance > 0  → cotton procurement above standard (paying more)
 *   Conv variance > 0 → spinning conversion above standard (less efficient)
 *
 * Source: GET /bale-mgmt/cost/variance
 */
import { useState } from 'react'
import {
  Card, Col, DatePicker, Empty, Row, Select, Space, Statistic, Table, Tag, Typography,
} from 'antd'
import {
  ArrowDownOutlined, ArrowUpOutlined, MinusCircleOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography
const { RangePicker } = DatePicker

interface VarianceRow {
  fg_lot_id: string
  fg_lot_no: string
  lot_date: string
  count_ne: number | null
  yarn_type: string | null
  weight_kg: number
  std_rm: number
  actual_rm: number
  rm_variance: number
  rm_variance_pct: number | null
  std_conv: number
  actual_conv: number
  conv_variance: number
  conv_variance_pct: number | null
  std_total: number
  actual_total: number
  total_variance: number
  verdict: 'FAVORABLE' | 'ON_TARGET' | 'UNFAVORABLE'
}

interface VarianceResponse {
  rows: VarianceRow[]
  summary: {
    lot_count: number
    total_kg: number
    std_total: number
    actual_total: number
    rm_variance: number
    conv_variance: number
    total_variance: number
    weighted_avg_std: number | null
    weighted_avg_actual: number | null
  }
}

const fmt = (n?: number | null, dp = 2) =>
  n != null ? n.toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : '—'

const verdictTag = (v: VarianceRow['verdict']) => {
  if (v === 'FAVORABLE') return <Tag color="green" icon={<ArrowDownOutlined />}>Favorable</Tag>
  if (v === 'UNFAVORABLE') return <Tag color="red" icon={<ArrowUpOutlined />}>Unfavorable</Tag>
  return <Tag color="default" icon={<MinusCircleOutlined />}>On Target</Tag>
}

export default function CostVariancePage() {
  const { plantId } = usePlantId()
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(() => [
    dayjs().subtract(30, 'day'),
    dayjs(),
  ])
  const [yarnFilter, setYarnFilter] = useState<string | undefined>()

  const { data, isLoading } = useQuery<VarianceResponse>({
    queryKey: ['cost-variance', plantId, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'), yarnFilter],
    queryFn: () =>
      apiClient
        .get('/bale-mgmt/cost/variance', {
          params: {
            plant_id: plantId,
            from_date: range[0].format('YYYY-MM-DD'),
            to_date: range[1].format('YYYY-MM-DD'),
            yarn_type: yarnFilter,
          },
        })
        .then(r => r.data),
    enabled: !!plantId,
  })

  const s = data?.summary
  const rows = data?.rows ?? []

  return (
    <div>
      <PageHeader
        title="Standard vs Actual Cost Variance"
        subtitle="Per FG lot — actual cost (RM + Conversion) compared with the configured Standard Cost. Identifies where mills drift from plan."
        extra={
          <Space>
            <Select
              allowClear
              placeholder="Yarn type filter"
              style={{ width: 180 }}
              value={yarnFilter}
              onChange={setYarnFilter}
              options={[
                { value: 'CARDED', label: 'Carded' },
                { value: 'COMBED', label: 'Combed' },
                { value: 'COMPACT', label: 'Compact' },
                { value: 'COMBED_COMPACT', label: 'Combed Compact' },
                { value: 'CARDED_COMPACT', label: 'Carded Compact' },
                { value: 'SIRO', label: 'Siro' },
              ]}
            />
            <RangePicker
              value={range}
              onChange={v => v && v[0] && v[1] && setRange([v[0], v[1]])}
              format="DD-MMM-YYYY"
              allowClear={false}
            />
          </Space>
        }
      />

      {/* KPI summary tiles */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #1677ff' }}>
            <Statistic title="FG Lots" value={s?.lot_count ?? 0}
              valueStyle={{ fontSize: 22, color: '#1677ff', fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
            <Statistic title="Wtd Avg Std ₹/kg"
              value={s?.weighted_avg_std ? fmt(s.weighted_avg_std) : '—'}
              valueStyle={{ fontSize: 22, color: '#52c41a', fontWeight: 700 }}
              prefix="₹" />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
            <Statistic title="Wtd Avg Actual ₹/kg"
              value={s?.weighted_avg_actual ? fmt(s.weighted_avg_actual) : '—'}
              valueStyle={{ fontSize: 22, color: '#faad14', fontWeight: 700 }}
              prefix="₹" />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{
            borderTop: `3px solid ${(s?.total_variance ?? 0) > 0 ? '#ff4d4f' : '#52c41a'}`,
          }}>
            <Statistic title="Total Variance"
              value={s?.total_variance != null ? fmt(s.total_variance, 0) : '—'}
              valueStyle={{
                fontSize: 22, fontWeight: 700,
                color: (s?.total_variance ?? 0) > 0 ? '#ff4d4f' : '#52c41a',
              }}
              prefix={(s?.total_variance ?? 0) > 0 ? '↑ ₹' : '↓ ₹'} />
          </Card>
        </Col>
      </Row>

      {/* Decomposition: RM vs Conversion */}
      {s && s.lot_count > 0 && (
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={12}>
            <Card size="small">
              <Space direction="vertical" size={0}>
                <Text type="secondary">RM Variance (₹ total across window)</Text>
                <Text strong style={{ fontSize: 18, color: s.rm_variance > 0 ? '#ff4d4f' : '#52c41a' }}>
                  {s.rm_variance > 0 ? '+' : ''}₹{fmt(s.rm_variance, 0)}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {s.rm_variance > 0
                    ? 'Cotton procurement above standard'
                    : 'Cotton procurement at or below standard'}
                </Text>
              </Space>
            </Card>
          </Col>
          <Col xs={12}>
            <Card size="small">
              <Space direction="vertical" size={0}>
                <Text type="secondary">Conversion Variance (₹ total across window)</Text>
                <Text strong style={{ fontSize: 18, color: s.conv_variance > 0 ? '#ff4d4f' : '#52c41a' }}>
                  {s.conv_variance > 0 ? '+' : ''}₹{fmt(s.conv_variance, 0)}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {s.conv_variance > 0
                    ? 'Spinning conversion above standard'
                    : 'Spinning conversion at or below standard'}
                </Text>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      <Card size="small" title="Per FG Lot">
        {!plantId ? <Empty description="Select a plant" /> : (
          <Table
            size="small"
            rowKey="fg_lot_id"
            loading={isLoading}
            dataSource={rows}
            pagination={{ pageSize: 25 }}
            scroll={{ x: 1500 }}
            columns={[
              { title: 'FG Lot', dataIndex: 'fg_lot_no', width: 130, fixed: 'left' as const,
                render: (v: string) => <Text code>{v}</Text> },
              { title: 'Date', dataIndex: 'lot_date', width: 100,
                render: (v: string) => v ? dayjs(v).format('DD-MMM') : '—' },
              { title: 'Count', dataIndex: 'count_ne', width: 60, align: 'right' as const,
                render: (v: number | null) => v ? `${Math.round(v)}s` : '—' },
              { title: 'Yarn', dataIndex: 'yarn_type', width: 100,
                render: (v?: string) => v || '—' },
              { title: 'kg', dataIndex: 'weight_kg', width: 90, align: 'right' as const,
                render: (v: number) => fmt(v, 0) },
              { title: 'Std RM', dataIndex: 'std_rm', width: 90, align: 'right' as const,
                render: (v: number) => `₹${fmt(v)}` },
              { title: 'Actual RM', dataIndex: 'actual_rm', width: 100, align: 'right' as const,
                render: (v: number) => <Text strong>₹{fmt(v)}</Text> },
              { title: 'RM Var', dataIndex: 'rm_variance', width: 90, align: 'right' as const,
                render: (v: number) => (
                  <Text style={{ color: v > 0 ? '#ff4d4f' : v < 0 ? '#52c41a' : undefined }}>
                    {v > 0 ? '+' : ''}{fmt(v)}
                  </Text>
                ) },
              { title: 'Std Conv', dataIndex: 'std_conv', width: 90, align: 'right' as const,
                render: (v: number) => `₹${fmt(v)}` },
              { title: 'Actual Conv', dataIndex: 'actual_conv', width: 100, align: 'right' as const,
                render: (v: number) => <Text strong>₹{fmt(v)}</Text> },
              { title: 'Conv Var', dataIndex: 'conv_variance', width: 90, align: 'right' as const,
                render: (v: number) => (
                  <Text style={{ color: v > 0 ? '#ff4d4f' : v < 0 ? '#52c41a' : undefined }}>
                    {v > 0 ? '+' : ''}{fmt(v)}
                  </Text>
                ) },
              { title: 'Total Var', dataIndex: 'total_variance', width: 100, align: 'right' as const,
                render: (v: number) => (
                  <Text strong style={{ color: v > 0 ? '#ff4d4f' : v < 0 ? '#52c41a' : undefined }}>
                    {v > 0 ? '+' : ''}{fmt(v)}
                  </Text>
                ) },
              { title: 'Verdict', dataIndex: 'verdict', width: 130, fixed: 'right' as const,
                render: verdictTag },
            ]}
          />
        )}
      </Card>
    </div>
  )
}
